import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  trendLabel?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}

export default function KPICard({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  icon,
  color = 'primary',
}: KPICardProps) {
  const getTrendColor = () => {
    if (trend === undefined) return 'text.secondary';
    return trend >= 0 ? 'success.main' : 'error.main';
  };

  const TrendIcon = trend && trend >= 0 ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '0.65rem' }}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ mt: 0.25, color: `${color}.main`, fontSize: '1rem', lineHeight: 1.2 }}
              noWrap
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.7rem' }}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.5 }}>
                <TrendIcon sx={{ fontSize: 12, color: getTrendColor() }} />
                <Typography variant="caption" sx={{ color: getTrendColor(), fontWeight: 600, fontSize: '0.65rem' }}>
                  {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                </Typography>
                {trendLabel && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {trendLabel}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                p: 0.5,
                borderRadius: 1,
                bgcolor: `${color}.50`,
                color: `${color}.main`,
                '& svg': { fontSize: 18 },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
